import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import BinanceLogo from "../../../public/binance.webp";
import CoinbaseLogo from "../../../public/coinbase.webp";
import KrakenLogo from "../../../public/kraken.webp";
import BitfinexLogo from "../../../public/bitfinex.webp";
import BitstampLogo from "../../../public/bitstamp.webp";
import KuCoinLogo from "../../../public/kucoin.webp";
import HuobiLogo from "../../../public/huobi.webp";
import GeminiLogo from "../../../public/gemini.webp";
import BittrexLogo from "../../../public/bittrex.svg";
import OkexLogo from "../../../public/okex.webp";
import DeribitLogo from "../../../public/wallets/deribit-logo.webp";
import { table, getBorderCharacters } from "table";
import { unparse } from "papaparse";
import { ExtendedAssetMetrics, UnifiedTransaction } from "@/types/portfolio";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function formatTokenAmount(
    amount: number,
    decimals: number = 9,
): string {
    const divisor = Math.pow(10, decimals);
    const formattedAmount = amount / divisor;

    if (formattedAmount > 0 && formattedAmount < 0.000001) {
        return formattedAmount.toExponential(4);
    }

    if (formattedAmount > 0) {
        const log10 = Math.floor(Math.log10(formattedAmount));
        const minDecimals = Math.max(2, -log10 + 1);

        return formattedAmount.toLocaleString(undefined, {
            minimumFractionDigits: Math.min(minDecimals, decimals),
            maximumFractionDigits: Math.max(minDecimals, decimals),
        });
    }

    return formattedAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: decimals,
    });
}

export const sourceIconMap: Record<number, string> = {
    1: BinanceLogo,
    2: CoinbaseLogo,
    3: KrakenLogo,
    4: BitfinexLogo,
    5: BitstampLogo,
    6: KuCoinLogo,
    7: HuobiLogo,
    8: GeminiLogo,
    9: BittrexLogo,
    10: OkexLogo,
    11: DeribitLogo,
};

export const formatPercent = (number: number) => {
    number = number * 100;
    return `${number.toFixed(2)}%`;
};

export const formatNumber = (number: number) => {
    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 9,
    }).format(number);
};

export const formatToken = (number: number) => {
    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 9,
    }).format(number);
};

export const exportToCSV = (
    assets: ExtendedAssetMetrics[],
    fileName: string,
) => {
    const data = assets.map((asset) => ({
        Name: asset.name,
        Symbol: asset.symbol,
        Source: asset.source,
        Tokens: asset.tokens,
        Price: asset.price,
        Allocation: asset.allocation,
        Balance: asset.balance,
        Tokens_Purchased: asset.tokens_purchased,
        Fiat_Purchased: asset.acquisition_cost_purchased,
        Avg_Price_Purchased: asset.average_price_purchased,
        Tokens_Sold: asset.tokens_sold,
        Fiat_Sold: asset.acquisition_cost_sold,
        Avg_Price_Sold: asset.average_price_sold,
    }));

    const csv = unparse(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const exportToTXT = (
    assets: ExtendedAssetMetrics[],
    fileName: string,
) => {
    const headers = [
        "Nome",
        "Símbolo",
        "Alocação",
        "Tokens",
        "Preço Atual",
        "Tokens comprados",
        "Fiat usado na compra",
        "Preço médio de compra",
        "Tokens vendidos",
        "Fiat recebido na venda",
        "Preço médio de venda",
        "Saldo Total",
        "Lucro anual",
        "Prejuízo anual",
        "Origem",
    ];

    const rows = assets.map((asset) => [
        asset.name ?? "",
        asset.symbol ?? "",
        `${(asset.allocation ?? 0 * 100).toFixed(2)}%`,
        (asset.tokens ?? 0).toFixed(4),
        `R$ ${(asset.price ?? 0).toFixed(2)}`,
        (asset.tokens_purchased ?? 0).toFixed(4),
        `R$ ${(asset.acquisition_cost_purchased ?? 0).toFixed(2)}`,
        `R$ ${(asset.average_price_purchased ?? 0).toFixed(2)}`,
        (asset.tokens_sold ?? 0).toFixed(4),
        `R$ ${(asset.acquisition_cost_sold ?? 0).toFixed(2)}`,
        `R$ ${(asset.average_price_sold ?? 0).toFixed(2)}`,
        `R$ ${(asset.balance ?? 0).toFixed(2)}`,
        "-", // lucro anual
        "-", // prejuízo anual
        asset.source ?? "",
    ]);

    const output = table([headers, ...rows], {
        columnDefault: {
            paddingLeft: 1,
            paddingRight: 1,
            wrapWord: false, // ✅ evita quebra de palavra
        },
        border: getBorderCharacters("honeywell"), // ✅ borda melhor
        drawHorizontalLine: (index, size) => {
            return index === 0 || index === 1 || index === size;
        },
    });

    const blob = new Blob([output], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export function exportUnifiedTokensToCSV(
    tokens: Array<{
        symbol: string;
        title: string;
        value_usd: number;
        amount: number;
        sources: Record<string, { amount: number; logo: string }>;
    }>,
    fileName = "meus-ativos",
    currency: "USD" | "BRL" = "USD",
    usdToBrlRate?: number | null,
) {
    const rows = tokens.map((token) => {
        const valueInCurrency = currency === "BRL" && usdToBrlRate
            ? token.value_usd * usdToBrlRate
            : token.value_usd;
        
        const currencySymbol = currency === "BRL" ? "R$" : "$";
        const sources = Object.keys(token.sources).join(", ");

        return {
            "Símbolo": token.symbol,
            "Nome": token.title,
            "Quantidade": token.amount.toFixed(8),
            [`Valor (${currency})`]: `${currencySymbol}${valueInCurrency.toFixed(2)}`,
            "Fontes": sources,
        };
    });

    const csv = unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function exportUnifiedTransactionsToCSV(
    transactions: UnifiedTransaction[],
    fileName = "transactions",
) {
    const rows = transactions.map((tx) => {
        const txDate = new Date(tx.timestamp);
        const dateStr = txDate.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });

        const isReceived = tx.direction === "received";
        const prefix = isReceived ? "+" : "-";
        const amount = parseFloat(tx.amount.toString()).toPrecision(6);
        const fiatValue = tx.fiat_value
            ? parseFloat(tx.fiat_value.toString())
            : 0;
        const brlValue = tx.ptax_rate ? fiatValue * tx.ptax_rate : 0;

        return {
            "Blockchain ou Corretora": tx.account_name || tx.source,
            "Data e Hora": dateStr,
            Direção: tx.direction,
            Tipo: tx.type,
            Subtipo: tx.subtype,
            Qtd: `${prefix}${amount} ${tx.ticker?.toUpperCase() || ""}`,
            "Valor (USD)": tx.fiat_value
                ? `${prefix}$${fiatValue.toFixed(2)}`
                : "–",
            "Valor (BRL)": tx.ptax_rate
                ? `${prefix}R$${brlValue.toFixed(2)}`
                : "–",
            "PTAX (USD → BRL)": tx.ptax_rate
                ? `R$ ${Number(tx.ptax_rate).toFixed(4)}`
                : "–",
        };
    });

    const csv = unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
export function getClosestAvailablePtax(
    date: Date,
    ptaxMap: Map<string, string | number>,
    brazilianHolidays: string[],
    maxLookbackDays: number = 30, // Increased from 7 to 30 days
): number {
    const cursor = new Date(date);
    const originalDate = date.toISOString().slice(0, 10);

    // First try to find any rate for the exact date
    const exactRate = ptaxMap.get(originalDate);
    if (exactRate !== undefined) {
        const num =
            typeof exactRate === "string" ? parseFloat(exactRate) : exactRate;
        if (!isNaN(num)) return num;
    }

    // If not found, look back up to maxLookbackDays
    for (let i = 0; i <= maxLookbackDays; i++) {
        const key = cursor.toISOString().slice(0, 10);
        const isWeekend = cursor.getDay() === 0 || cursor.getDay() === 6;
        const isHoliday = brazilianHolidays.includes(key);

        if (!isWeekend && !isHoliday) {
            const rate = ptaxMap.get(key);
            if (rate !== undefined) {
                const num = typeof rate === "string" ? parseFloat(rate) : rate;
                if (!isNaN(num)) return num;
            }
        }

        cursor.setDate(cursor.getDate() - 1);
    }

    return 1; // Fallback rate
}

export const debankTypeMapping: Record<
    string,
    string | { received: string; sent: string }
> = {
    // ✅ Airdrop
    airdrop: "Airdrop",
    distributeTokensFromContract: "Airdrop",
    tryAggregate: "Airdrop",

    Airdrop: "Airdrop",
    swapExactPtForToken: "Swap/Venda",
    redeem: { received: "Entrada Regular", sent: "Saída Regular" },
    vestEsGs: { received: "Entrada Regular", sent: "Saída Regular" },
    unstakeEsGs: { received: "Entrada Regular", sent: "Saída Regular" },
    redeemSyToToken: { received: "Entrada Regular", sent: "Saída Regular" },
    claimAndDelegate: { received: "Entrada Regular", sent: "Saída Regular" },
    requestClaim: { received: "Entrada Regular", sent: "Saída Regular" },
    unstake: { received: "Entrada Regular", sent: "Saída Regular" },

    // ✅ Regular entry/exit
    deposit: "Entrada Regular",
    withdraw: { received: "Entrada Regular", sent: "Saída Regular" },
    transfer: { received: "Entrada Regular", sent: "Saída Regular" },
    send: { received: "Entrada Regular", sent: "Saída Regular" },
    receive: "Entrada Regular",
    compound: { received: "Entrada Regular", sent: "Saída Regular" },
    redeemSolve: { received: "Entrada Regular", sent: "Saída Regular" },
    unoswap: { received: "Entrada Regular", sent: "Saída Regular" },
    multiSend: { received: "Entrada Regular", sent: "Saída Regular" },
    multisend: "Saída Regular",
    multisendToken: "Saída Regular",
    disperseToken: { received: "Entrada Regular", sent: "Saída Regular" },
    mintSavingcoin: { received: "Entrada Regular", sent: "Saída Regular" },
    mintStablecoin: { received: "Entrada Regular", sent: "Saída Regular" },
    mintOptions: { received: "Entrada Regular", sent: "Saída Regular" },
    mint: "Não declarável",
    forceExercise: { received: "Entrada Regular", sent: "Saída Regular" },
    multicall: { received: "Entrada Regular", sent: "Saída Regular" },
    processClaim: "Entrada Regular",
    queueWithdrawals: "Saída Regular",
    vestGs: { received: "Entrada Regular", sent: "Saída Regular" },
    unstakeGs: { received: "Entrada Regular", sent: "Saída Regular" },
    stakeGs: { received: "Entrada Regular", sent: "Saída Regular" },
    claimRetail: { received: "Entrada Regular", sent: "Saída Regular" },
    attestRubyscore: { received: "Entrada Regular", sent: "Saída Regular" },
    setScore: { received: "Entrada Regular", sent: "Saída Regular" },
    processWithdrawals: { received: "Entrada Regular", sent: "Saída Regular" },
    batchedFinalizeWithdrawals: {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },
    registerWithdrawal: { received: "Entrada Regular", sent: "Saída Regular" },
    cooldownShares: { received: "Entrada Regular", sent: "Saída Regular" },

    // ✅ Staking
    stake: "Staking",
    claim: { received: "Entrada Regular", sent: "Saída Regular" },

    // ✅ Swap/trade
    swap: "Swap/Venda",
    swapCompact: "Swap/Venda",
    swapMultiCompact: "Swap/Venda",
    swapExactPforToken: "Swap/Venda",
    swapExactTokenForPt: "Swap/Venda",
    swapExactAmountIn: "Swap/Venda",
    swapWithPermit: { received: "Entrada Regular", sent: "Saída Regular" },
    execute: "Swap/Venda",
    fillLimitOrderPermit2: {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },
    fillLimitOrder: { received: "Entrada Regular", sent: "Saída Regular" },
    fulfillOrder: { received: "Entrada Regular", sent: "Saída Regular" },
    directExecuteSwap: "Swap/Venda",

    // ✅ Bridge
    startBridgeTokensViaAcross: "Outros (Bridge)",
    startBridgeTokensViaStargate: "Outros (Bridge)",
    startBridgeTokensViaCBridge: "Outros (Bridge)",
    callBridgeCall: "Outros (Bridge)",

    // ✅ Loans / lending
    repay: "Empréstimo",
    borrow: "Empréstimo",
    leverageDeposit: "Entrada Regular",
    redeemUnderlying: "Swap/Venda",

    // ✅ Misc / non-reportable
    executeContract: "Não declarável",
    execTransaction: "Não declarável",
    execTransactionWithRole: {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },
    delegate: "Não declarável",
    approveDelegation: "Não declarável",
    createSubscription: "Não declarável",
    supply: { received: "Entrada Regular", sent: "Saída Regular" },
    mintFromToken: "Não declarável",
    wrapWithPermit: { received: "Entrada Regular", sent: "Saída Regular" },
    depositWithERC20WithPermit: {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },
    depositIntoStrategy: { received: "Entrada Regular", sent: "Saída Regular" },
    multiAssetDeposit: { received: "Entrada Regular", sent: "Saída Regular" },
    completeQueuedWithdrawals: {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },

    createSaltedOrder: { received: "Entrada Regular", sent: "Saída Regular" },
    ethUnoswap2: { received: "Entrada Regular", sent: "Saída Regular" },
    withdrawETH: { received: "Entrada Regular", sent: "Saída Regular" },
    depositETH: "Entrada Regular",
    increaseLockPositionAndBroadcast: "Garantia/Colateral",
    claimMultiple: { received: "Entrada Regular", sent: "Saída Regular" },
    swapExactTokenForYt: "Swap/Venda",
    redeemDueInterestAndRewards: {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },
    redeemPyToToken: { received: "Entrada Regular", sent: "Saída Regular" },
    lzReceive: { received: "Entrada Regular", sent: "Saída Regular" },
    claimWithdraw: { received: "Entrada Regular", sent: "Saída Regular" },
    requestWithdrawWithPermit: {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },
    mintPyFromToken: { received: "Entrada Regular", sent: "Saída Regular" },
    depositFor: { received: "Entrada Regular", sent: "Saída Regular" },
};

export const solanaTypeMapping: Record<
    string,
    string | { received: string; sent: string }
> = {
    Receive: "Entrada Regular",
    Swap: "Swap/Venda",
    Send: "Saída Regular",
    "Swap, Check Expected Vaults": "Saída Regular",
    "Swap, Restake": "Pool de Liquidez/Staking",
    "Receive, remove liquidity": "Entrada Regular",
    "Send, initialize position": "Pool de Liquidez/Staking",
    "Swap, new claim": "Entrada Regular",
    "Receive, register order": "Entrada Regular",
    "Send, refresh reserve": "Saída Regular",
    "Receive, set auction winner": "Entrada Regular",
    "Send, init order": "Saída Regular",
    "Send, deposit multiple tokens": {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },
    "Send, initialize Storage Tokens": "Saída Regular",
    "Send, Buy": "Saída Regular",
    "Send, Update Voter Weight": "Saída Regular",
    "Receive, Pre Flash Fill Order": "Entrada Regular",
    "Send, List": "Saída Regular",
    "Receive, Take Bid Full Meta": "Entrada Regular",
    "Send, init pool, deposit Sol": "Pool de Liquidez/Staking",
    "Send, Bid": "Saída Regular",
    "Receive, Lending Account": "Staking",
    "Receive, Refresh Reserve": "Entrada Regular",
    "Swap, Add Imbalance": "Pool de Liquidez/Staking",
    "Receive, withdraw": { received: "Entrada Regular", sent: "Saída Regular" },
    "Receive, withdraww": {
        received: "Entrada Regular",
        sent: "Saída Regular",
    },
    "Send, deposit": { received: "Entrada Regular", sent: "Saída Regular" },
    "Send, Create Stake Account": "Pool de Liquidez/Staking",
    "Send, lending account": "Saída Regular",
    "Swap, deposit": "Saída Regular",
    "Send, marginfi account": "Saída Regular",
    "Receive, Unstake": "Entrada Regular",
};

export async function copyToClipboard(
    text: string,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        onSuccess?.();
        return true;
    } catch (err) {
        console.error('Failed to copy:', err);
        onError?.(err);
        return false;
    }
}
